import { SITE_ORIGIN } from './homeJsonLd';
import { buildBlogPostSeo, type HeadMetaTag } from './blogSeo';
import {
  BLOG_JSON_LD_IDS,
  buildBlogJsonLdBlocks,
  type BlogPostJsonLdInput,
} from './blogJsonLd';

export function setMetaTag(attr: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

export function setCanonicalUrl(url: string) {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', url);
}

export interface DocumentSeo {
  title: string;
  canonical: string;
  metaTags: HeadMetaTag[];
}

/**
 * Write page-specific metadata into the document head and hand back a restore
 * function. Client-side navigation reuses one `<head>` for every route, so a
 * page that sets `article:*` or a post-specific `og:image` has to put back what
 * it found — otherwise the next route inherits the previous article's card.
 */
export function applyDocumentSeo({ title, canonical, metaTags }: DocumentSeo): () => void {
  const previousTitle = document.title;
  const previousCanonical =
    document.head.querySelector('link[rel="canonical"]')?.getAttribute('href') ?? null;
  const previousMeta = metaTags.map(({ attr, key }) => ({
    attr,
    key,
    content: document.head.querySelector(`meta[${attr}="${key}"]`)?.getAttribute('content') ?? null,
  }));

  document.title = title;
  setCanonicalUrl(canonical);
  for (const { attr, key, content } of metaTags) setMetaTag(attr, key, content);

  return () => {
    document.title = previousTitle;
    if (previousCanonical !== null) setCanonicalUrl(previousCanonical);
    for (const { attr, key, content } of previousMeta) {
      if (content === null) {
        document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
      } else {
        setMetaTag(attr, key, content);
      }
    }
  };
}

/** Write a JSON-LD block into the head, or drop the tag when `json` is null. */
function setJsonLd(id: string, json: string | null) {
  const existing = document.head.querySelector(`script[id="${id}"]`);

  if (json === null) {
    existing?.remove();
    return;
  }

  let script = existing;
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('id', id);
    script.setAttribute('type', 'application/ld+json');
    document.head.appendChild(script);
  }
  script.textContent = json;
}

/**
 * Article structured data, applied the same way the head tags are. Prerendered
 * pages already ship these blocks, but one `<head>` is reused across
 * client-side navigation — without this, the schema for the post you arrived on
 * outlives it and describes the next article under the wrong URL. Posts
 * published since the last build get their schema here too.
 */
export function applyBlogJsonLd(post: BlogPostJsonLdInput): () => void {
  const blocks = buildBlogJsonLdBlocks(post, SITE_ORIGIN);
  const previous = BLOG_JSON_LD_IDS.map((id) => ({
    id,
    json: document.head.querySelector(`script[id="${id}"]`)?.textContent ?? null,
  }));

  for (const { id } of previous) {
    setJsonLd(id, blocks.find((block) => block.id === id)?.json ?? null);
  }

  return () => {
    for (const { id, json } of previous) setJsonLd(id, json);
  };
}

/**
 * Apply an article's head metadata and structured data. Pinned to the
 * production origin rather than `window.location.origin` so the canonical URL a
 * preview or localhost build advertises still points at the real article.
 */
export function applyBlogPostSeo(post: BlogPostJsonLdInput): () => void {
  const seo = buildBlogPostSeo(post, SITE_ORIGIN);
  const restoreHead = applyDocumentSeo({
    title: seo.documentTitle,
    canonical: seo.canonical,
    metaTags: seo.metaTags,
  });
  const restoreJsonLd = applyBlogJsonLd(post);

  return () => {
    restoreJsonLd();
    restoreHead();
  };
}
